import React from 'react';
import type { InboundConfigCreate, InboundConfigResponse } from '../../../services/adminService';

type AdminPageInboundConfigModalProps = {
  userName: string;
  loading: boolean;
  saving: boolean;
  config: InboundConfigResponse | null;
  form: InboundConfigCreate;
  setForm: (f: InboundConfigCreate) => void;
  onClose: () => void;
  onSave: () => void;
};

const AdminPageInboundConfigModal: React.FC<AdminPageInboundConfigModalProps> = ({
  userName,
  loading,
  saving,
  config,
  form,
  setForm,
  onClose,
  onSave,
}) => (
  <div className="admin-page-modal-overlay">
    <div className="admin-page-modal admin-page-modal-xl custom-scrollbar">
      <div className="admin-page-modal-header">
        <div>
          <h3 className="admin-page-modal-title">Inbound Configuration</h3>
          <p className="admin-page-modal-subtitle mt-1">Viewing configuration for {userName}</p>
        </div>
        <button
          type="button"
          onClick={onClose}
          className="admin-page-modal-close"
          aria-label="Close modal"
          disabled={loading}
        >
          <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
          </svg>
        </button>
      </div>

      {loading ? (
        <div className="admin-page-modal-loading">
          <div className="admin-page-modal-loading-spinner" />
        </div>
      ) : (
        <div className="admin-page-modal-body space-y-4">
          <div className="admin-page-inbound-form">
            <div>
              <label className="admin-page-modal-label-block">Assistant ID</label>
              <input
                type="text"
                value={form.assistant_id}
                onChange={(e) => setForm({ ...form, assistant_id: e.target.value })}
                className="admin-page-modal-input"
                placeholder="Inbound Assistant ID"
                disabled={saving}
              />
            </div>
            <div>
              <label className="admin-page-modal-label-block">Campaign Context (Prompt)</label>
              <textarea
                rows={5}
                value={form.campaign_context}
                onChange={(e) => setForm({ ...form, campaign_context: e.target.value })}
                className="admin-page-modal-input admin-page-modal-textarea"
                placeholder="System prompt to use for inbound calls"
                disabled={saving}
              />
            </div>
            <div className="admin-page-inbound-toggle-wrap">
              <label className="admin-page-modal-label-block">Active</label>
              <label className="admin-page-toggle-label">
                <input
                  type="checkbox"
                  className="sr-only"
                  checked={form.is_active}
                  onChange={(e) => setForm({ ...form, is_active: e.target.checked })}
                  disabled={saving}
                />
                <div className={`admin-page-toggle-track ${form.is_active ? 'admin-page-toggle-track-on' : ''}`} />
                <div className={`admin-page-toggle-dot ${form.is_active ? 'admin-page-toggle-dot-on' : ''}`} />
              </label>
              <span className="admin-page-inbound-toggle-text">{form.is_active ? 'Active' : 'Inactive'}</span>
            </div>
            {(config?.created_at || config?.updated_at) && (
              <div className="admin-page-inbound-meta">
                {config?.created_at && (
                  <div>
                    <span className="admin-page-inbound-meta-label">Created</span>
                    <span>{new Date(config.created_at).toLocaleString()}</span>
                  </div>
                )}
                {config?.updated_at && (
                  <div>
                    <span className="admin-page-inbound-meta-label">Updated</span>
                    <span>{new Date(config.updated_at).toLocaleString()}</span>
                  </div>
                )}
              </div>
            )}
          </div>
          {config === null && (
            <div className="admin-page-warning-yellow">
              No inbound configuration exists yet. Saving will create one and sync to Voice Gateway.
            </div>
          )}
        </div>
      )}

      <div className="admin-page-modal-actions admin-page-modal-actions-end">
        <button type="button" onClick={onClose} className="admin-page-modal-btn admin-page-modal-btn-cancel">
          Close
        </button>
        <button
          type="button"
          onClick={onSave}
          disabled={saving || loading}
          className="admin-page-modal-btn admin-page-modal-btn-primary"
        >
          {saving ? 'Saving...' : 'Save & Sync'}
        </button>
      </div>
    </div>
  </div>
);

export default AdminPageInboundConfigModal;
